"use client";

import { useState, useEffect, Suspense } from "react";
import { useSearchParams } from "next/navigation";
import Link from "next/link";
import { createClient } from "@/lib/supabase/client";
import { trackEvent, captureException } from "@/lib/posthog";

type Mode = "signin" | "signup";

interface AuthError {
  message: string;
  hint: string;
  reason: string;
  icon: "error" | "lock" | "mail" | "schedule";
}

/** Translate common Supabase auth errors to user-friendly French with tracking reason. */
function translateAuthError(message: string): AuthError {
  const lower = message.toLowerCase();
  if (lower.includes("invalid login credentials") || lower.includes("invalid_credentials")) {
    return {
      message: "E-mail ou mot de passe incorrect.",
      hint: "Vérifiez vos identifiants et réessayez.",
      reason: "invalid_credentials",
      icon: "error",
    };
  }
  if (lower.includes("email not confirmed")) {
    return {
      message: "Votre e-mail n'a pas encore été confirmé.",
      hint: "Vérifiez votre boîte de réception (et les spams).",
      reason: "email_not_confirmed",
      icon: "mail",
    };
  }
  if (lower.includes("too many requests") || lower.includes("rate limit")) {
    return {
      message: "Trop de tentatives.",
      hint: "Veuillez patienter quelques minutes avant de réessayer.",
      reason: "rate_limited",
      icon: "schedule",
    };
  }
  if (lower.includes("user banned") || lower.includes("blocked") || lower.includes("banned")) {
    return {
      message: "Compte temporairement bloqué.",
      hint: "Trop de tentatives. Réessayez dans quelques minutes ou contactez-nous.",
      reason: "account_banned",
      icon: "lock",
    };
  }
  if (lower.includes("signup is disabled")) {
    return {
      message: "Les inscriptions sont temporairement désactivées.",
      hint: "Réessayez plus tard ou contactez-nous.",
      reason: "signup_disabled",
      icon: "lock",
    };
  }
  return {
    message: "Une erreur est survenue.",
    hint: "Veuillez réessayer.",
    reason: "unknown",
    icon: "error",
  };
}

/** Translate signup API errors to structured AuthError. */
function translateSignupError(apiError: string, status: number): AuthError {
  if (status === 403 && apiError.includes("invalide")) {
    return {
      message: "Code d'invitation invalide.",
      hint: "Vérifiez le code reçu et réessayez.",
      reason: "invalid_invite_code",
      icon: "error",
    };
  }
  if (status === 403 && apiError.includes("limite")) {
    return {
      message: "Ce code d'invitation a déjà été utilisé.",
      hint: "Demandez un nouveau code pour vous inscrire.",
      reason: "invite_code_exhausted",
      icon: "lock",
    };
  }
  if (status === 409) {
    return {
      message: "Cet e-mail est déjà utilisé.",
      hint: "Connectez-vous ou utilisez un autre e-mail.",
      reason: "email_already_registered",
      icon: "mail",
    };
  }
  if (status === 400) {
    return {
      message: "Informations manquantes.",
      hint: "Remplissez tous les champs et réessayez.",
      reason: "missing_fields",
      icon: "error",
    };
  }
  return {
    message: apiError || "Erreur lors de l'inscription.",
    hint: "Veuillez réessayer.",
    reason: "signup_error",
    icon: "error",
  };
}

/** Map URL error params from auth callback to user-facing AuthError. */
function translateCallbackError(errorParam: string): AuthError {
  switch (errorParam) {
    case "otp_expired":
      return {
        message: "Votre lien a expiré.",
        hint: "Demandez un nouveau lien ci-dessous.",
        reason: "otp_expired",
        icon: "schedule",
      };
    case "code_exchange_failed":
      return {
        message: "La connexion a échoué.",
        hint: "Veuillez réessayer de vous connecter.",
        reason: "code_exchange_failed",
        icon: "error",
      };
    case "server_error":
      return {
        message: "Une erreur serveur est survenue.",
        hint: "Veuillez réessayer dans quelques instants.",
        reason: "server_error",
        icon: "error",
      };
    default:
      return {
        message: "La connexion a échoué.",
        hint: "Veuillez réessayer.",
        reason: "auth_callback_failed",
        icon: "error",
      };
  }
}

const ERROR_ICONS: Record<AuthError["icon"], string> = {
  error: "error",
  lock: "lock",
  mail: "mark_email_unread",
  schedule: "schedule",
};

export default function LoginPage() {
  return (
    <Suspense fallback={<div className="bg-background min-h-screen" />}>
      <LoginPageInner />
    </Suspense>
  );
}

function LoginPageInner() {
  const searchParams = useSearchParams();
  const [mode, setMode] = useState<Mode>("signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [inviteCode, setInviteCode] = useState("");
  const [error, setError] = useState<AuthError | null>(null);
  const [loading, setLoading] = useState(false);
  const [signupSuccess, setSignupSuccess] = useState(false);

  // Forgot password state
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [resetLoading, setResetLoading] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);
  const [failedAttempts, setFailedAttempts] = useState(0);

  // Read error from URL params (set by auth callback)
  useEffect(() => {
    const errorParam = searchParams.get("error");
    if (errorParam) {
      const callbackError = translateCallbackError(errorParam);
      setError(callbackError);
      // Show forgot password form for expired links
      if (errorParam === "otp_expired") {
        setShowForgotPassword(true);
      }
      // Clean the URL without triggering navigation
      window.history.replaceState({}, "", "/login");
    }
  }, [searchParams]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);

    const supabase = createClient();

    try {
      if (mode === "signup") {
        // Validate invite code + create user via server action
        const res = await fetch("/api/auth/signup", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email, password, inviteCode }),
        });
        if (!res.ok) {
          let apiError = "Erreur lors de l'inscription.";
          try {
            const data = await res.json();
            apiError = data.error ?? apiError;
          } catch {
            // Server returned non-JSON (e.g. HTML error page)
          }
          const authErr = translateSignupError(apiError, res.status);
          setError(authErr);
          captureException(apiError, { context: "signup", status: res.status });
          trackEvent("signup_failed", { reason: authErr.reason, error: apiError });
          return;
        }
        // Show confirmation screen immediately
        setSignupSuccess(true);
        setLoading(false);
        trackEvent("signup_account_created", { invite_code: inviteCode });

        // Auto sign in with timeout
        const loginPromise = supabase.auth.signInWithPassword({
          email,
          password,
        });

        // Minimal error shape (not full AuthError) - only .message is used downstream
        const timeoutPromise = new Promise<{ error: { message: string } }>((resolve) =>
          setTimeout(() => resolve({ error: { message: "Auto-login timeout" } }), 3000)
        );

        const { error: signInError } = await Promise.race([loginPromise, timeoutPromise]);
        if (signInError) {
          captureException(signInError, { context: "signup_auto_login" });
          trackEvent("signup_auto_login_failed", { error: signInError.message });
          // User sees the confirmation screen - they can click "Se connecter" manually
          return;
        }
        trackEvent("signup_completed", { invite_code: inviteCode });

        // Small delay so user sees the confirmation before redirect
        await new Promise((r) => setTimeout(r, 1500));
        window.location.href = "/";
        return;
      } else {
        const { error: signInError } = await supabase.auth.signInWithPassword({
          email,
          password,
        });
        if (signInError) {
          const authErr = translateAuthError(signInError.message);
          setError(authErr);
          captureException(signInError, { context: "login" });
          trackEvent("login_failed", { reason: authErr.reason, raw_error: signInError.message });
          setFailedAttempts((prev) => prev + 1);
          return;
        }
        trackEvent("login_completed");
        setFailedAttempts(0);
      }

      // Full reload so the proxy picks up the new session cookies
      window.location.href = "/";
    } catch (err) {
      captureException(err, { context: mode });
      trackEvent(`${mode}_error`, { reason: "network_error", error: String(err) });
      setError({
        message: mode === "signup"
          ? "Impossible de créer votre compte."
          : "Impossible de se connecter.",
        hint: "Vérifiez votre connexion et réessayez.",
        reason: "network_error",
        icon: "error",
      });
    } finally {
      setLoading(false);
    }
  }

  async function handlePasswordReset(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setResetLoading(true);

    try {
      const res = await fetch("/api/auth/password-reset", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: resetEmail }),
      });

      if (!res.ok) {
        const data = await res.json();
        setError({
          message: data.error ?? "Erreur lors de l'envoi.",
          hint: "Vérifiez votre e-mail et réessayez.",
          reason: "password_reset_error",
          icon: "error",
        });
        trackEvent("password_reset_failed", { reason: "api_error" });
        return;
      }

      setResetSuccess(true);
      trackEvent("password_reset_requested");
    } finally {
      setResetLoading(false);
    }
  }

  return (
    <div className="relative flex min-h-screen flex-col items-center justify-center overflow-hidden px-5">
      {/* Animated luxury background */}
      <div className="bg-background absolute inset-0">
        {/* Warm terracotta blob — top left */}
        <div
          className="login-blob absolute -top-20 -left-20 h-[420px] w-[420px] rounded-full opacity-[0.12]"
          style={{
            background: "radial-gradient(circle, #b15d3f 0%, transparent 70%)",
            animation: "blob-drift-1 18s ease-in-out infinite",
          }}
        />
        {/* Soft amber blob — center right */}
        <div
          className="login-blob absolute top-1/3 -right-16 h-[350px] w-[350px] rounded-full opacity-[0.10]"
          style={{
            background: "radial-gradient(circle, #ffdbcf 0%, transparent 70%)",
            animation: "blob-drift-2 22s ease-in-out infinite",
          }}
        />
        {/* Deep warm blob — bottom */}
        <div
          className="login-blob absolute -bottom-24 left-1/4 h-[380px] w-[380px] rounded-full opacity-[0.08]"
          style={{
            background: "radial-gradient(circle, #70573f 0%, transparent 70%)",
            animation: "blob-drift-3 20s ease-in-out infinite",
          }}
        />
        {/* Subtle cream highlight — top right */}
        <div
          className="login-blob absolute -top-10 right-1/4 h-[280px] w-[280px] rounded-full opacity-[0.15]"
          style={{
            background: "radial-gradient(circle, #fdf9f4 0%, transparent 60%)",
            animation: "blob-drift-4 16s ease-in-out infinite",
          }}
        />
        {/* Blur layer over all blobs */}
        <div className="absolute inset-0 backdrop-blur-[80px]" />
      </div>

      <div className="bg-background/60 border-outline-variant/10 relative z-10 w-full max-w-sm rounded-3xl border p-6 shadow-sm shadow-stone-200/30 backdrop-blur-xl">
        {/* Back to website */}
        <Link
          href="/"
          className="text-on-surface-variant hover:text-on-surface mb-4 inline-flex items-center gap-1 text-sm transition-colors"
        >
          <span className="material-symbols-outlined text-base">arrow_back</span>
          Retour au site
        </Link>

        {/* Logo */}
        <div className="mb-10 text-center">
          <h1 className="font-headline login-logo text-5xl italic">Vitrine</h1>
          <p className="text-on-surface-variant mt-2 text-sm">
            {signupSuccess
              ? "Bienvenue !"
              : showForgotPassword
                ? "Réinitialiser votre mot de passe"
                : mode === "signin"
                  ? "Connectez-vous pour continuer"
                  : "Inscrivez-vous avec votre code d'invitation"}
          </p>
        </div>

        {/* ---- Signup confirmation screen ---- */}
        {signupSuccess ? (
          <div className="space-y-6 text-center">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-green-50">
              <span className="material-symbols-outlined text-3xl text-green-600">
                check_circle
              </span>
            </div>
            <div className="space-y-2">
              <h2 className="font-headline text-xl italic text-stone-900">
                Compte créé avec succès !
              </h2>
              <p className="text-sm text-stone-500">
                Votre compte Vitrine est prêt. Connexion en cours...
              </p>
            </div>
            <div className="flex items-center justify-center gap-2 text-sm text-stone-400">
              <span className="material-symbols-outlined animate-spin text-base">progress_activity</span>
              Redirection automatique...
            </div>
            <button
              onClick={() => {
                setSignupSuccess(false);
                setMode("signin");
              }}
              className="btn-primary mt-2 w-full py-3 text-sm"
            >
              Se connecter
            </button>
          </div>
        ) : (
        <>
        {/* ---- Forgot password form ---- */}
        {showForgotPassword ? (
          <div className="space-y-5">
            {resetSuccess ? (
              <div className="flex items-start gap-2 rounded-xl border border-green-200/60 bg-green-50 p-3">
                <span className="material-symbols-outlined shrink-0 text-lg text-green-600">
                  check_circle
                </span>
                <p className="text-sm text-green-900">
                  Un lien de réinitialisation a été envoyé à votre email.
                </p>
              </div>
            ) : (
              <form onSubmit={handlePasswordReset} className="space-y-5">
                <div>
                  <label className="font-label text-on-surface-variant mb-2 block text-xs tracking-widest uppercase">
                    Email
                  </label>
                  <input
                    type="email"
                    value={resetEmail}
                    onChange={(e) => setResetEmail(e.target.value)}
                    placeholder="vous@exemple.com"
                    required
                    className="bg-surface-container-low border-outline-variant/30 text-on-surface placeholder:text-outline focus:ring-primary/40 w-full rounded-xl border px-4 py-3 text-sm focus:ring-2 focus:outline-none"
                  />
                </div>

                {error && (
                  <div className="flex items-start gap-2 rounded-xl border border-red-200/60 bg-red-50 p-3">
                    <span className="material-symbols-outlined shrink-0 text-lg text-red-500">
                      {ERROR_ICONS[error.icon]}
                    </span>
                    <div className="space-y-1">
                      <p className="text-sm text-red-900">{error.message}</p>
                      <p className="text-xs text-red-700/70">{error.hint}</p>
                    </div>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={resetLoading}
                  className="btn-primary w-full py-4 hover:opacity-90 disabled:opacity-50"
                >
                  {resetLoading ? "Envoi..." : "Envoyer le lien"}
                </button>
              </form>
            )}

            <div className="mt-6 text-center">
              <button
                onClick={() => {
                  setShowForgotPassword(false);
                  setResetSuccess(false);
                  setResetEmail("");
                  setError(null);
                }}
                className="btn-tertiary"
              >
                Retour à la connexion
              </button>
            </div>
          </div>
        ) : (
          <>
            {/* ---- Google OAuth ---- */}
            {mode === "signin" && (
              <>
                <div className="relative">
                  <button
                    type="button"
                    disabled
                    className="bg-surface-container-low border-outline-variant/30 text-on-surface/40 flex w-full cursor-not-allowed items-center justify-center gap-3 rounded-full border py-3.5 text-sm font-medium"
                  >
                    <svg className="h-5 w-5 opacity-40" viewBox="0 0 24 24">
                      <path
                        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
                        fill="#4285F4"
                      />
                      <path
                        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                        fill="#34A853"
                      />
                      <path
                        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                        fill="#FBBC05"
                      />
                      <path
                        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                        fill="#EA4335"
                      />
                    </svg>
                    Continuer avec Google
                  </button>
                  <p className="text-on-surface-variant/60 mt-1.5 text-center text-[11px]">
                    Bientôt disponible
                  </p>
                </div>

                {/* Divider */}
                <div className="my-6 flex items-center gap-4">
                  <div className="bg-outline-variant/30 h-px flex-1" />
                  <span className="text-on-surface-variant font-label text-xs tracking-widest uppercase">
                    ou
                  </span>
                  <div className="bg-outline-variant/30 h-px flex-1" />
                </div>
              </>
            )}

            {/* ---- Email / password form ---- */}
            <form onSubmit={handleSubmit} className="space-y-5">
              {mode === "signup" && (
                <div>
                  <label className="font-label text-on-surface-variant mb-2 block text-xs tracking-widest uppercase">
                    Code d&apos;invitation
                  </label>
                  <input
                    type="text"
                    value={inviteCode}
                    onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
                    placeholder="VITRINE-BETA-XXX"
                    required
                    className="bg-surface-container-low border-outline-variant/30 text-on-surface placeholder:text-outline focus:ring-primary/40 w-full rounded-xl border px-4 py-3 font-mono text-sm focus:ring-2 focus:outline-none"
                  />
                </div>
              )}

              <div>
                <label className="font-label text-on-surface-variant mb-2 block text-xs tracking-widest uppercase">
                  Email
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="vous@exemple.com"
                  required
                  className="bg-surface-container-low border-outline-variant/30 text-on-surface placeholder:text-outline focus:ring-primary/40 w-full rounded-xl border px-4 py-3 text-sm focus:ring-2 focus:outline-none"
                />
              </div>

              <div>
                <label className="font-label text-on-surface-variant mb-2 block text-xs tracking-widest uppercase">
                  Mot de passe
                </label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  minLength={6}
                  className="bg-surface-container-low border-outline-variant/30 text-on-surface placeholder:text-outline focus:ring-primary/40 w-full rounded-xl border px-4 py-3 text-sm focus:ring-2 focus:outline-none"
                />
              </div>

              {error && (
                <div className="space-y-2">
                  <div className="flex items-start gap-2 rounded-xl border border-red-200/60 bg-red-50 p-3">
                    <span className="material-symbols-outlined shrink-0 text-lg text-red-500">
                      {ERROR_ICONS[error.icon]}
                    </span>
                    <div className="space-y-1">
                      <p className="text-sm text-red-900">{error.message}</p>
                      <p className="text-xs text-red-700/70">{error.hint}</p>
                    </div>
                  </div>
                  {failedAttempts >= 2 && error.reason === "invalid_credentials" && (
                    <button
                      type="button"
                      onClick={() => {
                        setShowForgotPassword(true);
                        setResetEmail(email);
                        setError(null);
                      }}
                      className="flex w-full items-center justify-center gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm font-medium text-amber-900 transition-colors hover:bg-amber-100"
                    >
                      <span className="material-symbols-outlined text-base">lock_reset</span>
                      Réinitialiser mon mot de passe
                    </button>
                  )}
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="btn-primary w-full py-4 hover:opacity-90 disabled:opacity-50"
              >
                {loading
                  ? "Chargement..."
                  : mode === "signin"
                    ? "Se connecter"
                    : "Créer mon compte"}
              </button>
            </form>

            {/* Forgot password link (only in signin mode) */}
            {mode === "signin" && (
              <div className="mt-4 text-center">
                <button
                  onClick={() => {
                    setShowForgotPassword(true);
                    setError(null);
                  }}
                  className="text-on-surface-variant hover:text-primary text-xs transition-colors"
                >
                  Mot de passe oublié ?
                </button>
              </div>
            )}

            <div className="mt-8 text-center">
              <button
                onClick={() => {
                  setMode(mode === "signin" ? "signup" : "signin");
                  setError(null);
                  setShowForgotPassword(false);
                  setResetSuccess(false);
                }}
                className="btn-tertiary"
              >
                {mode === "signin"
                  ? "Première visite ? Créer un compte"
                  : "Déjà inscrit ? Se connecter"}
              </button>
            </div>
          </>
        )}
        </>
        )}
      </div>
    </div>
  );
}
